import { useState, useEffect } from "react";
import { getAgents, createAgent, updateAgent, changePassword, getClients, assignAgentClient } from "../api";
import { useAuth } from "../context/AuthContext";
import useIsMobile from "../hooks/useIsMobile";
import { Plus, Key, Edit2, Building2 } from "lucide-react";

const inputStyle = { background:"var(--t-surface-mid)", border:"1px solid var(--t-border-strong)", borderRadius:8, padding:"8px 12px", color:"var(--t-text-primary)", fontSize:13, width:"100%" };

const roleConfig = {
  admin:       { bg:"rgba(239,68,68,0.12)",    color:"#dc2626", label:"Admin" },
  manager:     { bg:"rgba(96,165,250,0.15)",   color:"#2563eb", label:"Manager" },
  technician:  { bg:"rgba(52,211,153,0.12)",   color:"#059669", label:"Technician" },
  read_only:   { bg:"rgba(148,163,184,0.15)",  color:"#64748b", label:"Read Only" },
  dev:         { bg:"rgba(167,139,250,0.15)",  color:"#7b61ff", label:"Dev" },
};

export default function Agents() {
  const { agent: me } = useAuth();
  const isMobile = useIsMobile();
  const [agents,     setAgents]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [showNew,    setShowNew]    = useState(false);
  const [editAgent,  setEditAgent]  = useState(null);
  const [showPwd,    setShowPwd]    = useState(false);
  const [form,       setForm]       = useState({ email:"", password:"", first_name:"", last_name:"", role:"technician", dev:false });
  const [editForm,   setEditForm]   = useState({ role:"technician", dev:false, active:true });
  const [pwdForm,    setPwdForm]    = useState({ currentPassword:"", newPassword:"", confirm:"" });
  const [pwdError,   setPwdError]   = useState("");
  const [pwdSuccess, setPwdSuccess] = useState(false);
  const [clients,    setClients]    = useState([]);
  const [clientAgent,setClientAgent]= useState(null);

  useEffect(() => {
    getAgents()
      .then(r => setAgents(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
    getClients().then(r => setClients(r.data)).catch(() => {});
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const roles = [form.role, ...(form.dev ? ["dev"] : [])];
      const res = await createAgent({ ...form, roles });
      setAgents(prev => [...prev, res.data]);
      setShowNew(false);
      setForm({ email:"", password:"", first_name:"", last_name:"", role:"technician", dev:false });
    } catch (err) { alert(err.response?.data?.error || "Failed to create agent"); }
  };

  const openEdit = (a) => {
    setEditAgent(a);
    setEditForm({
      role:   a.role || "technician",
      dev:    a.roles?.includes("dev") || false,
      active: a.is_active !== false,
    });
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      const roles = [editForm.role, ...(editForm.dev ? ["dev"] : [])];
      const res = await updateAgent(editAgent.id, { role: editForm.role, roles, active: editForm.active });
      setAgents(prev => prev.map(a => a.id === editAgent.id ? { ...a, ...res.data } : a));
      setEditAgent(null);
    } catch (err) { alert(err.response?.data?.error || "Failed to update agent"); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwdError("");
    if (pwdForm.newPassword !== pwdForm.confirm) { setPwdError("New passwords do not match"); return; }
    if (pwdForm.newPassword.length < 8) { setPwdError("Password must be at least 8 characters"); return; }
    try {
      await changePassword({ currentPassword: pwdForm.currentPassword, newPassword: pwdForm.newPassword });
      setPwdSuccess(true);
      setPwdForm({ currentPassword:"", newPassword:"", confirm:"" });
      setTimeout(() => { setShowPwd(false); setPwdSuccess(false); }, 2000);
    } catch (err) { setPwdError(err.response?.data?.error || "Failed to change password"); }
  };

  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100%" }}>
      <div style={{ width:28, height:28, border:"3px solid #4f6fff", borderTopColor:"transparent", borderRadius:"50%" }} className="animate-spin" />
    </div>
  );

  const isAdmin = me?.role === "admin" || me?.roles?.includes("admin");

  return (
    <div style={{ padding: isMobile ? 14 : 24, maxWidth:1000 }} className="fade-in">
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10, marginBottom:24 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:700, color:"var(--t-text-primary)" }}>Agents</h1>
          <p style={{ color:"var(--t-text-label)", fontSize:13, marginTop:2 }}>{agents.length} team members</p>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={() => setShowPwd(true)} style={{ background:"var(--t-surface-mid)", color:"var(--t-text-muted)", padding:"8px 14px", borderRadius:8, fontSize:13, display:"flex", alignItems:"center", gap:6 }}>
            <Key size={14} /> Change Password
          </button>
          {isAdmin && (
            <button onClick={() => setShowNew(true)} style={{ background:"#4f6fff", color:"#fff", padding:"8px 16px", borderRadius:8, fontSize:13, fontWeight:600, display:"flex", alignItems:"center", gap:6 }}>
              <Plus size={15} /> Add Agent
            </button>
          )}
        </div>
      </div>

      {/* Agent grid */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(280px, 1fr))", gap:12 }}>
        {agents.map(a => {
          const primaryRole = roleConfig[a.role] || roleConfig.technician;
          const hasDev = a.roles?.includes("dev");
          const isMe   = a.id === me?.id;
          return (
            <div key={a.id} style={{ background: "var(--t-panel)", border:`1px solid ${isMe ? "rgba(99,162,255,0.35)" : "var(--t-border-soft)"}`, borderRadius:12, padding:18, opacity: a.is_active === false ? 0.6 : 1, boxShadow:"var(--t-shadow-panel)" }}>
              <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:12 }}>
                <div style={{ width:44, height:44, background: isMe ? "rgba(99,162,255,0.18)" : "var(--t-surface-mid)", borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, fontWeight:700, color: isMe ? "#3b82f6" : "var(--t-text-muted)", flexShrink:0 }}>
                  {a.first_name?.[0]}{a.last_name?.[0]}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:14, fontWeight:600, color:"var(--t-text-primary)", display:"flex", alignItems:"center", gap:6 }}>
                    {a.first_name} {a.last_name}
                    {isMe && <span style={{ fontSize:10, color:"#60a5fa", background:"rgba(99,162,255,0.15)", padding:"1px 6px", borderRadius:4 }}>You</span>}
                  </div>
                  <div style={{ fontSize:12, color:"var(--t-text-label)", marginTop:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{a.email}</div>
                </div>
                <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:4, flexShrink:0 }}>
                  <span style={{ background:primaryRole.bg, color:primaryRole.color, fontSize:10, fontWeight:600, padding:"2px 8px", borderRadius:20 }}>{primaryRole.label}</span>
                  {hasDev && <span style={{ background:roleConfig.dev.bg, color:roleConfig.dev.color, fontSize:10, fontWeight:600, padding:"2px 8px", borderRadius:20 }}>Dev</span>}
                </div>
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", fontSize:11, color:"var(--t-text-deep)" }}>
                <span>{a.is_active !== false ? "✓ Active" : "✗ Inactive"}</span>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <span>Last login: {a.last_login ? new Date(a.last_login).toLocaleDateString() : "Never"}</span>
                  {isAdmin && !isMe && (
                    <div style={{ display:"flex", gap:4 }}>
                      {(a.role === "manager" || a.roles?.includes("manager")) && (
                        <button onClick={() => setClientAgent(a)} style={{ background:"var(--t-surface-mid)", color:"var(--t-text-label)", padding:"3px 8px", borderRadius:6, fontSize:11, display:"flex", alignItems:"center", gap:4, cursor:"pointer", border:"none" }} title="Assign client">
                          <Building2 size={10} /> Client
                        </button>
                      )}
                      <button onClick={() => openEdit(a)} style={{ background:"var(--t-surface-mid)", color:"var(--t-text-label)", padding:"3px 8px", borderRadius:6, fontSize:11, display:"flex", alignItems:"center", gap:4, cursor:"pointer", border:"none" }}>
                        <Edit2 size={10} /> Edit
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* New Agent Modal */}
      {showNew && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:50, padding:20 }}>
          <div style={{ background: "var(--t-panel)", border:"1px solid var(--t-border-strong)", borderRadius:16, padding:28, width:"100%", maxWidth:440 }} className="fade-in">
            <h2 style={{ fontSize:17, fontWeight:600, color:"var(--t-text-primary)", marginBottom:20 }}>Add New Agent</h2>
            <form onSubmit={handleCreate} style={{ display:"flex", flexDirection:"column", gap:12 }}>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                <div>
                  <label style={{ fontSize:11, color:"var(--t-text-muted)", display:"block", marginBottom:5 }}>First Name *</label>
                  <input value={form.first_name} onChange={e => setForm(p => ({ ...p, first_name:e.target.value }))} required style={inputStyle} placeholder="John" />
                </div>
                <div>
                  <label style={{ fontSize:11, color:"var(--t-text-muted)", display:"block", marginBottom:5 }}>Last Name *</label>
                  <input value={form.last_name} onChange={e => setForm(p => ({ ...p, last_name:e.target.value }))} required style={inputStyle} placeholder="Smith" />
                </div>
              </div>
              <div>
                <label style={{ fontSize:11, color:"var(--t-text-muted)", display:"block", marginBottom:5 }}>Email *</label>
                <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email:e.target.value }))} required style={inputStyle} placeholder="john@example.com" />
              </div>
              <div>
                <label style={{ fontSize:11, color:"var(--t-text-muted)", display:"block", marginBottom:5 }}>Temporary Password *</label>
                <input type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password:e.target.value }))} required style={inputStyle} placeholder="Min 8 characters" />
              </div>
              <div>
                <label style={{ fontSize:11, color:"var(--t-text-muted)", display:"block", marginBottom:5 }}>Primary Role</label>
                <select value={form.role} onChange={e => setForm(p => ({ ...p, role:e.target.value }))} style={{ ...inputStyle, cursor:"pointer" }}>
                  <option value="technician">Technician</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                  <option value="read_only">Read Only</option>
                </select>
              </div>
              <label style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer", padding:"8px 12px", background:"var(--t-surface-mid)", borderRadius:8 }}>
                <input type="checkbox" checked={form.dev} onChange={e => setForm(p => ({ ...p, dev:e.target.checked }))} style={{ width:16, height:16, cursor:"pointer" }} />
                <div>
                  <div style={{ fontSize:13, color:"var(--t-text-primary)", fontWeight:500 }}>Dev Access</div>
                  <div style={{ fontSize:11, color:"var(--t-text-label)" }}>Grants access to dev-only features and infrastructure tools</div>
                </div>
              </label>
              <div style={{ display:"flex", gap:10, marginTop:6 }}>
                <button type="submit" style={{ flex:1, background:"#4f6fff", color:"#fff", padding:"10px", borderRadius:8, fontWeight:600, fontSize:14 }}>Create Agent</button>
                <button type="button" onClick={() => setShowNew(false)} style={{ flex:1, background:"var(--t-surface-mid)", color:"var(--t-text-muted)", padding:"10px", borderRadius:8, fontSize:14 }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Agent Modal */}
      {editAgent && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:50, padding:20 }}>
          <div style={{ background: "var(--t-panel)", border:"1px solid var(--t-border-strong)", borderRadius:16, padding:28, width:"100%", maxWidth:400 }} className="fade-in">
            <h2 style={{ fontSize:17, fontWeight:600, color:"var(--t-text-primary)", marginBottom:4 }}>Edit Agent</h2>
            <p style={{ fontSize:13, color:"var(--t-text-label)", marginBottom:20 }}>{editAgent.first_name} {editAgent.last_name} · {editAgent.email}</p>
            <form onSubmit={handleEdit} style={{ display:"flex", flexDirection:"column", gap:12 }}>
              <div>
                <label style={{ fontSize:11, color:"var(--t-text-muted)", display:"block", marginBottom:5 }}>Primary Role</label>
                <select value={editForm.role} onChange={e => setEditForm(p => ({ ...p, role:e.target.value }))} style={{ ...inputStyle, cursor:"pointer" }}>
                  <option value="technician">Technician</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                  <option value="read_only">Read Only</option>
                </select>
              </div>
              <label style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer", padding:"8px 12px", background:"var(--t-surface-mid)", borderRadius:8 }}>
                <input type="checkbox" checked={editForm.dev} onChange={e => setEditForm(p => ({ ...p, dev:e.target.checked }))} style={{ width:16, height:16, cursor:"pointer" }} />
                <div>
                  <div style={{ fontSize:13, color:"var(--t-text-primary)", fontWeight:500 }}>Dev Access</div>
                  <div style={{ fontSize:11, color:"var(--t-text-label)" }}>Grants access to dev-only features</div>
                </div>
              </label>
              <label style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer", padding:"8px 12px", background:"var(--t-surface-mid)", borderRadius:8 }}>
                <input type="checkbox" checked={editForm.active} onChange={e => setEditForm(p => ({ ...p, active:e.target.checked }))} style={{ width:16, height:16, cursor:"pointer" }} />
                <div>
                  <div style={{ fontSize:13, color:"var(--t-text-primary)", fontWeight:500 }}>Active</div>
                  <div style={{ fontSize:11, color:"var(--t-text-label)" }}>Uncheck to deactivate this agent's account</div>
                </div>
              </label>
              <div style={{ display:"flex", gap:10, marginTop:6 }}>
                <button type="submit" style={{ flex:1, background:"#4f6fff", color:"#fff", padding:"10px", borderRadius:8, fontWeight:600, fontSize:14 }}>Save Changes</button>
                <button type="button" onClick={() => setEditAgent(null)} style={{ flex:1, background:"var(--t-surface-mid)", color:"var(--t-text-muted)", padding:"10px", borderRadius:8, fontSize:14 }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPwd && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:50, padding:20 }}>
          <div style={{ background: "var(--t-panel)", border:"1px solid var(--t-border-strong)", borderRadius:16, padding:28, width:"100%", maxWidth:400 }} className="fade-in">
            <h2 style={{ fontSize:17, fontWeight:600, color:"var(--t-text-primary)", marginBottom:20 }}>Change Password</h2>
            {pwdSuccess
              ? <div style={{ background:"rgba(16,185,129,0.1)", border:"1px solid #10b981", borderRadius:8, padding:16, color:"#059669", textAlign:"center", fontSize:14 }}>✓ Password changed successfully!</div>
              : (
                <form onSubmit={handleChangePassword} style={{ display:"flex", flexDirection:"column", gap:12 }}>
                  {pwdError && <div style={{ background:"rgba(220,38,38,0.1)", border:"1px solid #dc2626", borderRadius:8, padding:10, color:"#dc2626", fontSize:13 }}>{pwdError}</div>}
                  {[["currentPassword","Current Password"],["newPassword","New Password"],["confirm","Confirm New Password"]].map(([k, l]) => (
                    <div key={k}>
                      <label style={{ fontSize:11, color:"var(--t-text-muted)", display:"block", marginBottom:5 }}>{l}</label>
                      <input type="password" value={pwdForm[k]} onChange={e => setPwdForm(p => ({ ...p, [k]:e.target.value }))} required style={inputStyle} placeholder="••••••••" />
                    </div>
                  ))}
                  <div style={{ display:"flex", gap:10, marginTop:6 }}>
                    <button type="submit" style={{ flex:1, background:"#4f6fff", color:"#fff", padding:"10px", borderRadius:8, fontWeight:600, fontSize:14 }}>Update Password</button>
                    <button type="button" onClick={() => { setShowPwd(false); setPwdError(""); }} style={{ flex:1, background:"var(--t-surface-mid)", color:"var(--t-text-muted)", padding:"10px", borderRadius:8, fontSize:14 }}>Cancel</button>
                  </div>
                </form>
              )
            }
          </div>
        </div>
      )}
      {/* Assign Client Modal (for managers) */}
      {clientAgent && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:50, padding:20 }}>
          <div style={{ background: "var(--t-panel)", border:"1px solid var(--t-border-strong)", borderRadius:16, padding:28, width:"100%", maxWidth:400 }} className="fade-in">
            <h2 style={{ fontSize:17, fontWeight:600, color:"var(--t-text-primary)", marginBottom:4 }}>Assign Client</h2>
            <p style={{ fontSize:13, color:"var(--t-text-label)", marginBottom:20 }}>
              {clientAgent.first_name} {clientAgent.last_name} — scopes KB article creation to this client
            </p>
            <select
              defaultValue={clientAgent.client_id || ""}
              id="assign-client-select"
              style={{ ...inputStyle, marginBottom:16 }}
            >
              <option value="">— No client (admin-level) —</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
            <div style={{ display:"flex", gap:10 }}>
              <button
                style={{ flex:1, background:"#4f6fff", color:"#fff", padding:"10px", borderRadius:8, fontWeight:600, fontSize:14 }}
                onClick={async () => {
                  const val = document.getElementById("assign-client-select")?.value;
                  try {
                    await assignAgentClient(clientAgent.id, val || null);
                    setAgents(prev => prev.map(a => a.id === clientAgent.id ? { ...a, client_id: val || null } : a));
                    setClientAgent(null);
                  } catch (err) { alert(err.response?.data?.error || "Failed"); }
                }}
              >Save</button>
              <button onClick={() => setClientAgent(null)} style={{ flex:1, background:"var(--t-surface-mid)", color:"var(--t-text-muted)", padding:"10px", borderRadius:8, fontSize:14 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
